import { Stack, Typography } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { t } from 'i18next';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SlidingPanel } from 'core/components';
import { Loader, TextEllipsis } from 'core/elements';
import { AppEnv } from 'core/enums';
import { APP_ENV, PHPSESSID } from 'global';
import { Color, Level } from 'theme/enums';

import { parseCsv } from './elements/parseCsv';
import { PreviewCsvTable } from './PreviewCsvTable';

interface IProps {
  // One of the two: a file to fetch, or CSV text already in hand (no request is made).
  fileUrl?: string;
  content?: string;
  fileName?: string;
  subtitle?: string;
  onClose?: () => void;
  onLoaded?: () => void;
  onError?: (error: Error) => void;
  isOpen?: boolean;
  inline?: boolean;
  wrapperSx?: SxProps;
}

const buildFetchHeaders = (): HeadersInit => ({
  'X-Requested-With': 'XMLHttpRequest',
  ...(APP_ENV === AppEnv.DEVELOPMENT && PHPSESSID ? { phpsessid: PHPSESSID } : {}),
});

interface IContentProps {
  fileUrl?: string;
  content?: string;
  onLoaded?: () => void;
  onError?: (error: Error) => void;
  wrapperSx?: SxProps;
}

const PreviewCsvContent = ({ fileUrl, content, onLoaded, onError, wrapperSx }: IContentProps) => {
  const { t: translate } = useTranslation();

  const [rows, setRows] = useState<string[][] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const onLoadedRef = useRef(onLoaded);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onLoadedRef.current = onLoaded;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    setRows(null);
    setError(null);

    if (undefined !== content) {
      setRows(parseCsv(content));
      onLoadedRef.current?.();

      return;
    }

    if (!fileUrl) {
      return;
    }

    const controller = new AbortController();

    const fetchAndParse = async () => {
      try {
        const response = await fetch(fileUrl, {
          credentials: 'include',
          headers: buildFetchHeaders(),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }

        const text = await response.text();

        if (controller.signal.aborted) {
          return;
        }

        setRows(parseCsv(text));
        onLoadedRef.current?.();
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }

        const errInstance = err instanceof Error ? err : new Error(String(err));
        setError(errInstance);
        onErrorRef.current?.(errInstance);
      }
    };

    fetchAndParse();

    return () => {
      controller.abort();
    };
  }, [fileUrl, content]);

  if (error) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          minHeight: 240,
          ...wrapperSx,
        }}
      >
        <Typography level={Level.BODY_MD} color={Color.DANGER}>
          {translate('messagePreviewCsvError')}
        </Typography>
      </Stack>
    );
  }

  if (!rows) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          minHeight: 240,
          ...wrapperSx,
        }}
      >
        <Loader />
      </Stack>
    );
  }

  if (rows.length === 0) {
    return (
      <Typography level={Level.BODY_MD} color={Color.NEUTRAL}>
        {translate('messagePreviewCsvEmpty')}
      </Typography>
    );
  }

  const [headerRow, ...dataRows] = rows;

  return (
    <Stack sx={{ gap: 1, minHeight: 0, flex: 1, ...wrapperSx }}>
      <Typography level={Level.BODY_SM} color={Color.NEUTRAL}>
        {translate('messagePreviewCsvRowCount', { count: dataRows.length })}
      </Typography>

      <PreviewCsvTable headerRow={headerRow} dataRows={dataRows} />
    </Stack>
  );
};

const buildPanelTitle = (fileName?: string): ReactNode => (
  <TextEllipsis
    content={fileName || t('labelPreviewCsv')}
    style={{ fontSize: '24px' }}
    lineHeight={32}
  />
);

export const PreviewCsv = ({
  fileUrl,
  content,
  fileName,
  subtitle,
  onClose,
  onLoaded,
  onError,
  isOpen = false,
  inline = false,
  wrapperSx,
}: IProps) => {
  if (inline) {
    return (
      <PreviewCsvContent
        fileUrl={fileUrl}
        content={content}
        onLoaded={onLoaded}
        onError={onError}
        wrapperSx={wrapperSx}
      />
    );
  }

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={onClose}
      title={buildPanelTitle(fileName)}
      subtitle={subtitle}
      autoWidth
    >
      {isOpen && (
        <PreviewCsvContent
          fileUrl={fileUrl}
          content={content}
          onLoaded={onLoaded}
          onError={onError}
          wrapperSx={wrapperSx ?? { maxHeight: 'calc(100vh - 120px)' }}
        />
      )}
    </SlidingPanel>
  );
};
